import { CircleText, QuestionDisplayMode } from '@doltech/ui/lib/figma/CircleText/CircleText';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import {
  DropableBox,
  DropableWrapper,
  QuestionBox,
  QuestionDropable,
} from '@doltech/ui/lib/figma/Question/QuestionDropable';
import { SmallCloseButton } from '@doltech/ui/lib/figma/QuestionWithSpots/SpotEditor';
import { isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import { KeyValueModelSnapshotIn } from '../question-data-model/key-value.store';
import { DragDropComponent, DragDropComponentProps } from './components/DragDropComponent';

interface RenderDragDrop {
  circleText: string;
  text: string;
}

interface DropResult {
  dragData: any;
  dropData: any;
}

interface DroppedRenderInput {
  userAnswer: number;
  dropOption: KeyValueModelSnapshotIn;
}

interface MatchingHeadingAnswerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    DragDropComponentProps {
  onRemoveAnswerAtIndex: (optionIndex: number) => void;
  dropOption: KeyValueModelSnapshotIn[];
  onDropSuccess: (input: DropResult) => void;
  droppedRender: (input: DroppedRenderInput) => RenderDragDrop;
  getUserAnswer?: (input: any) => void;
  onOptionChange?: ({ key, value }) => void;
}

export const MatchingAnswerDragDrop = observer((props: MatchingHeadingAnswerProps) => {
  const {
    id,
    no,
    userAnswers = [],
    onRemoveAnswerAtIndex,
    dropOption,
    onDropSuccess,
    droppedRender,
    accept,
    getUserAnswer,
    onOptionChange,
    ...rest
  } = props;

  const userAnswersMap = userAnswers.map((userAnswer) => ({
    userAnswer,
    userAnswerKey: userAnswer,
  }));

  return (
    <DragDropComponent
      id={id}
      className="drag-area"
      accept={accept}
      userAnswers={userAnswersMap}
      {...rest}
    >
      <div className="place-to-drop">
        {dropOption.map((dOption, dropOptionIndex) => {
          const { value, key } = dOption;
          let nodes;
          const onDrop = ({ dragData }) => {
            onDropSuccess({
              dragData,
              dropData: dOption,
            });
          };
          const userAnswer = getUserAnswer
            ? getUserAnswer({ userAnswers, dropOption: dOption, dropOptionIndex })
            : userAnswers.find((_ua, uaIndex) => uaIndex === dropOptionIndex);
          if (isNil(userAnswer) === false) {
            const { circleText, text } = droppedRender({ userAnswer, dropOption: dOption });
            nodes = [
              {
                color: colorsV2.blue100,
                renderElement: (
                  <DropableWrapper onDrop={onDrop} accept={accept}>
                    {({ canDrop, isOver }) => (
                      <QuestionBox isMatching canDrop={canDrop} isOver={isOver} dropable>
                        <CircleText
                          displayMode={QuestionDisplayMode.QUESTION_OPTION}
                          circleText={circleText}
                          text={text}
                          selected
                        />
                        <SmallCloseButton
                          className="btn-close"
                          onClick={() => {
                            document.getElementById(text)?.classList.remove('hover');
                            onRemoveAnswerAtIndex(dOption.key);
                          }}
                        />
                      </QuestionBox>
                    )}
                  </DropableWrapper>
                ),
              },
            ];
          } else {
            nodes = [
              {
                color: colorsV2.gray20,
                renderElement: <DropableBox onDrop={onDrop} accept={accept} />,
              },
            ];
          }

          return (
            <QuestionDropable
              onOptionChange={(updatedValue) => onOptionChange?.({ key, value: updatedValue })}
              key={dropOptionIndex}
              no={Number(no) + dropOptionIndex}
              title={`${value}`}
              nodes={nodes}
            />
          );
        })}
      </div>
    </DragDropComponent>
  );
});
